import { Col, Row, Space } from 'antd';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { generatePath, useNavigate } from 'react-router-dom';

import { EditIcon, SearchIcon } from '@/assets/icons';
import ButtonWrap from '@/components/ButtonWrap';
import FormItemWrap from '@/components/FormItemWrap';
import IconWrap from '@/components/IconWrap';
import InputWrap from '@/components/InputWrap';
import TableAdmin from '@/components/TableAdmin';
import LayoutContent from '@/layout/Content';
import { appRoutes } from '@/router/routes';
import customerCardService from '@/services/customer-card.service';
import { handleMessageError } from '@/util/handleMessageError';

const UnregisteredCustomers = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { control, handleSubmit } = useForm();
    const dateFormat = 'YYYY-MM-DD';
    const navigate = useNavigate();

    const cardNo = useWatch({
        control: control,
        name: 'cardNo',
    });

    const { data: customerCardsData, refetch } = useQuery(
        ['customer-card', cardNo],
        () => customerCardService.findCardNo(cardNo),
        {
            enabled: false,
            onError: (error: any) => {
                handleMessageError(error, t);
            },
            initialData: [],
        },
    );

    const customerCards = customerCardsData?.map((item: any) => ({
        id: item.id,
        key: item.id,
        cardNo: item.cardNo,
        createdDate: moment(item.createdAt).format(dateFormat),
        registrant: item.entryStaffName,
    }));

    useEffect(() => {
        if (cardNo) {
            queryClient.removeQueries('customer-card', cardNo);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardNo]);

    const columns = useMemo(
        () => [
            {
                title: t('customer:MEM_NO'),
                dataIndex: 'cardNo',
            },
            {
                title: t('customer:CREATED_DATE'),
                dataIndex: 'createdDate',
                align: 'center',
            },
            {
                title: t('customer:REGISTRANT'),
                dataIndex: 'registrant',
                align: 'center',
            },
            {
                title: t('seat-list-type:ACTION'),
                render: (record: any) => (
                    <Row justify="center">
                        <Space size={20}>
                            <IconWrap
                                className="pointer"
                                icon={<EditIcon />}
                                onClick={() => {
                                    navigate(
                                        generatePath(`/${appRoutes.CUSTOMER.DETAIL_REGISTER}`, {
                                            customerCardId: record.id,
                                        }),
                                    );
                                }}
                            />
                        </Space>
                    </Row>
                ),
                width: 150,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t, customerCards],
    );

    const onSubmit = () => {
        refetch();
    };

    return (
        <LayoutContent
            className="p-unregistered-customers"
            title={`${t('MASTER_ADMIN')} / ${t('customer:UNREGISTERED_CUSTOMERS_TITLE')}`}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Row gutter={24}>
                    <Col span={18}>
                        <FormItemWrap labelAlign="left" label={t('customer:MEM_NO')} colon={false}>
                            <Controller
                                name="cardNo"
                                control={control}
                                render={({ field }) => <InputWrap field={field} />}
                            />
                        </FormItemWrap>
                    </Col>
                    <Col span={6}>
                        <ButtonWrap
                            icon={<IconWrap icon={<SearchIcon />} />}
                            text={`${t('SEARCH')}`}
                            type="primary"
                            width="100%"
                            htmlType="submit"
                        />
                    </Col>
                </Row>
            </form>
            <TableAdmin data={customerCards} columns={columns} />
        </LayoutContent>
    );
};

export default UnregisteredCustomers;
